import {
  type DataAggregator,
  type EmailElement,
  type FlowSummary,
  type PhoneElement,
  type StringSplitElement,
  type SetLocalStorageElement,
  type NumberElement,
  type ListBrowser,
  type FetchElement,
  type CustomElement,
  type TextElement,
  type NumberUnitField,
  type CheckboxField,
  type LicensePlateElement,
  type SelectElement,
  type ButtonListElement,
  type ButtonGridElement,
  type MultipleEntryElement,
  ImageType,
  type CameraElement,
} from '@logistiek-zonder-papier/lzp-library'

export function createNumberConfig(): NumberElement {
  return {
    id: undefined,
    type: 'number',
    label: 'Number Label',
    placeholder: 'Enter a number',
    defaultValue: '',
    storeLocation: '',
    required: true,
    error: '',
    description: '',
    min: '1',
    max: '100',
    limitMode: 'OFF',
  }
}

export function createListBrowserConfig(): ListBrowser {
  return {
    id: undefined,
    type: 'listBrowser',
    label: '',
    description: '',
    required: true,
    error: '',
    storeLocation: '',
    listSourcePath: '',
    titleTemplate: '',
    predicateCombinator: 'AND',
    mappingTemplate: '',
    descriptorTemplates: [],
    orderSetting: 'OFF',
    orderKey: '',
    predicates: [],
    groupByFields: [],
    autoComplete: true,
    exactMatch: false,
    exactMatchField: null,
    showPreviousSelected: false,
    previousSelectedLabel: 'Previous Selected',
  }
}

export function createSetLocalStorageConfig(): SetLocalStorageElement {
  return {
    id: undefined,
    name: 'Set Local Storage',
    type: 'setLocalStorage',
    sourcePath: '',
    key: '',
  }
}

export function createStringSplitConfig(): StringSplitElement {
  return {
    id: undefined,
    name: 'String Split',
    type: 'stringSplit',
    sourcePath: '',
    storeLocation: '',
    splitCharacter: ',',
    mode: 'split',
  }
}

export function createFetchConfig(): FetchElement {
  return {
    id: undefined,
    name: 'fetch',
    type: 'fetch',
    url: '',
    storeLocation: '',
    headers: {},
  }
}

export function createCustomConfig(): CustomElement {
  return {
    id: undefined,
    type: 'custom',
    customType: 'custom',
    name: 'custom',
    label: 'Custom label',
    json: '',
    jsonSchema: '',
  }
}

export function createWSBDConfig(): CustomElement {
  return {
    id: undefined,
    type: 'custom',
    customType: 'wsbd',
    name: 'wsbd',
    label: 'WSBD label',
    json: JSON.stringify({
      token: 'token',
      channel: 'wsbd-roosendaal',
      isPreview: true,
      journeyId: '1',
      unit: 'm3',
      storeLocation: 'discharge.amount',
    }),
    jsonSchema: JSON.stringify({
      type: 'object',
      required: ['token', 'channel'],
      properties: {
        token: { type: 'string' },
        channel: { type: 'string' },
        isPreview: { type: 'boolean' },
        journeyId: { type: 'string' },
        unit: { type: 'string' },
        storeLocation: { type: 'string' },
      },
    }),
  }
}

export function createPhoneConfig(): PhoneElement {
  return {
    id: undefined,
    type: 'phone',
    label: 'Phone Number',
    placeholder: 'Enter a phone number',
    storeLocation: '',
    required: true,
    error: '',
  }
}

export function createTextConfig(dto?: string): TextElement {
  return {
    id: undefined,
    type: 'text',
    label: 'Text Label',
    placeholder: 'Enter a text',
    defaultValue: '',
    storeLocation: dto ? `${dto}.` : '',
    required: true,
    error: '',
    description: '',
    minimumCharacterLimit: null,
    maximumCharacterLimit: null,
  }
}

export function createLicensePlateConfig(dto?: string): LicensePlateElement {
  return {
    id: undefined,
    type: 'licensePlate',
    label: 'License Plate',
    placeholder: 'AB-123-C',
    defaultValue: '',
    countryFormatting: 'none',
    countryValidation: 'none',
    storeLocation: dto ? `${dto}.` : '',
    required: true,
    error: '',
  }
}

export function createEmailConfig(dto?: string): EmailElement {
  return {
    id: undefined,
    type: 'email',
    label: 'Email Label',
    placeholder: 'Enter a text',
    defaultValue: '',
    storeLocation: dto ? `${dto}.` : '',
    required: true,
    error: '',
  }
}

export function createNumberUnitConfig(): NumberUnitField {
  return {
    id: undefined,
    type: 'numberUnit',
    label: 'Number Unit Label',
    placeholder: 'Enter a number',
    defaultValue: '',
    storeLocation: '',
    unit: 'kg',
    required: true,
    error: '',
    description: '',
    min: '1',
    max: '100',
    limitMode: 'OFF',
  }
}

export function createSelectConfig(): SelectElement {
  return {
    id: undefined,
    type: 'select',
    label: 'Select Label',
    placeholder: '',
    required: true,
    error: '',
    storeLocation: '',
    listSourcePath: '',
    labelTemplate: '',
    valueKey: '',
    defaultValue: '',
    emptyMessage: '',
  }
}

export function createButtonListConfig(): ButtonListElement {
  return {
    id: undefined,
    type: 'buttonList',
    label: 'Button List Label',
    required: true,
    error: '',
    storeLocation: '',
    buttons: [{ label: '', value: '' }],
  }
}

export function createButtonGridConfig(): ButtonGridElement {
  return {
    id: undefined,
    type: 'buttonGrid',
    label: 'Button Grid Label',
    required: true,
    error: '',
    storeLocation: '',
    buttons: [
      { label: '', value: '', icon: '', buttonColor: '', textColor: '', iconColor: '' },
      { label: '', value: '', icon: '', buttonColor: '', textColor: '', iconColor: '' },
    ],
  }
}

export function createMultipleEntryConfig(): MultipleEntryElement {
  return {
    id: undefined,
    type: 'multipleEntry',
    label: 'Multiple Entry Label',
    placeholder: '',
    error: '',
    storeLocation: '',
    inputType: 'text',
    minEntries: null,
    maxEntries: null,
    maximumCharacterLimit: null,
    duplicateMessage: '',
    maxEntriesMessage: '',
  }
}

export function createCheckboxConfig(): CheckboxField {
  return {
    id: undefined,
    type: 'checkbox',
    labelIsHtml: false,
    label: 'Text Label',
    storeLocation: '',
    requiredTrue: true,
    error: '',
  }
}

export function createSummaryConfig(): FlowSummary {
  return {
    id: undefined,
    type: 'summary',
    sections: [],
  }
}

export function createDataAggregatorConfig(): DataAggregator {
  return {
    id: undefined,
    name: 'aggregator',
    type: 'aggregator',
    sourceArray: '',
    elementTarget: '',
    storeLocation: '',
  }
}

export function createCameraConfig(): CameraElement {
  return {
    id: undefined,
    type: 'camera',
    imageType: ImageType.DATA_URL,
    quality: 90,
    storeLocation: '',
    min: 1,
    max: 1,
    label: '',
    error: '',
  }
}
